import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, RotateCcw, RefreshCw, Send, Square, Check, AlertTriangle, Loader2, ExternalLink, Ban, Mic, ImagePlus, Camera, FileText, PenLine, BookOpen, BarChart3, Sun } from 'lucide-react';
import useSpeech from '../capture/useSpeech';
import { fileToAttachment, imageFilesFrom, MAX_IMAGES } from '../capture/images';
import { useToast } from '../context/ToastContext';
import { useAssistant } from './AssistantContext';
import { useCapture } from '../capture/CaptureContext';
import { ProposalStack } from '../components/ai';
import Markdown from './Markdown';
import ReportCard from './ReportCard';
import MeetingReportCard from './MeetingReportCard';
import { fieldsFor } from './editable';
import { startersFor } from './starters';
import './assistant.css';

// The drawer. For what the other surfaces cannot do in one step: questions
// over the record, instructions that touch several records, lists by
// description, a briefing. It streams, shows what it is looking up, and
// every change it proposes is a card the person approves here.

const KIND_LABELS = { activity: 'activity', task: 'action item', contact: 'contact', account: 'account', opportunity: 'opportunity', field: 'change', link: 'page', meeting_report: 'meeting write-up' };

const isMacLike = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const modKey = isMacLike ? '⌘' : 'Ctrl';

/** "2 AI calls · 46 of 200 left today", in words a rep reads at a glance. */
export function usageLine(m) {
  const calls = m.usage && m.usage.calls ? m.usage.calls : 0;
  const used = `${calls} AI call${calls === 1 ? '' : 's'}`;
  if (!m.allowance || typeof m.allowance.left !== 'number') return used;
  return `${used} · ${m.allowance.left} of ${m.allowance.cap} left today`;
}

function ToolLine({ tool }) {
  const Icon = tool.status === 'running' ? Loader2 : tool.status === 'done' ? Check : tool.status === 'rejected' ? Ban : AlertTriangle;
  return (
    <div className={`asst-tool asst-tool-${tool.status}`}>
      <Icon size={12} className={tool.status === 'running' ? 'asst-spin' : ''} aria-hidden="true" />
      <span>{tool.label}{tool.detail ? ` · ${tool.detail}` : ''}</span>
    </div>
  );
}

const DECIDED_WORD = { applied: 'Done', rejected: 'Not now', undone: 'Undone' };

function Proposals({ message, onDecide, busy, superseded, onApproveMeeting, onDismiss, onAsk, onTick, onDraftChange, onEditField }) {
  const navigate = useNavigate();
  const simple = (p) => p.kind !== 'link' && p.kind !== 'meeting_report';
  const pending = message.proposals.filter((p) => p.state === 'pending' && simple(p));
  const decided = message.proposals.filter((p) => p.state !== 'pending' && simple(p));
  const links = message.proposals.filter((p) => p.kind === 'link');
  const reports = message.proposals.filter((p) => p.kind === 'meeting_report');

  return (
    <div className="asst-proposals">
      {reports.map((p) => (
        <MeetingReportCard
          key={p.key}
          proposal={p}
          superseded={superseded && p.state === 'pending'}
          busy={busy}
          onApprove={(report, base) => onApproveMeeting(message.id, p.key, report, base)}
          onDiscard={() => onDismiss(message.id, p.key)}
          onAsk={onAsk}
          onChange={(report) => onDraftChange(message.id, p.key, report)}
        />
      ))}
      {pending.length > 0 && (
        <ProposalStack
          title={pending.length === 1 ? 'One change to approve' : `${pending.length} changes to approve`}
          items={pending.map((p) => {
            const fields = fieldsFor(p);
            // An activity's summary is one of its fields, so it is not shown twice.
            return { id: p.key, kind: p.kind, title: p.title, description: fields && p.kind === 'activity' ? null : p.description, changes: p.changes, reason: p.reason, accepted: p.accepted !== false, fields, edited: !!p.edited };
          })}
          onToggle={(key, accepted) => onTick(message.id, key, accepted)}
          onFieldEdit={(key, field, value) => onEditField(message.id, key, field, value)}
          onConfirm={() => onDecide(message.id, Object.fromEntries(pending.map((p) => [p.key, p.accepted !== false])))}
          onDismiss={() => onDecide(message.id, Object.fromEntries(pending.map((p) => [p.key, false])))}
          confirmLabel={pending.length > 1 && pending.every((p) => p.accepted !== false) ? 'Apply all' : 'Apply'}
          dismissLabel="Not now"
          showCount
          busy={busy}
          role="region"
          className="asst-stack"
        />
      )}
      {decided.length > 0 && (
        <ul className="asst-decided">
          {decided.map((p) => (
            <li key={p.key} className={`asst-decided-${p.state}`}>
              {p.state === 'applied' ? <Check size={12} aria-hidden="true" /> : p.state === 'failed' ? <AlertTriangle size={12} aria-hidden="true" /> : p.state === 'undone' ? <RotateCcw size={12} aria-hidden="true" /> : <Ban size={12} aria-hidden="true" />}
              <span>{p.state === 'failed' ? `Could not apply: ${p.error}` : (DECIDED_WORD[p.state] || 'Not now')} · {KIND_LABELS[p.kind] || p.kind}: {p.title}</span>
              {p.state === 'applied' && p.applied && p.applied.path && (
                <button type="button" className="asst-open" onClick={() => navigate(p.applied.path)} aria-label={`Open ${p.title}`}>
                  Open <ExternalLink size={11} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {links.map((p) => (
        <button key={p.key} type="button" className="btn btn-secondary btn-sm asst-link" onClick={() => navigate(p.payload.path)}>
          <ExternalLink size={13} aria-hidden="true" /> {p.payload.label || p.title}
        </button>
      ))}
    </div>
  );
}

export default function AssistantDrawer() {
  const { open, messages, busy, width, setWidth, close, send, stop, clear, decide, draft, setDraft, applyMeeting, dismissProposal, retry, editFailed, regenerate, canRegenerate, setProposalAccepted, saveMeetingDraft, editProposal, viewing, clearViewing, restoreViewing, hint, focusTick } = useAssistant();
  const { pageContext } = useCapture();
  const location = useLocation();
  const toast = useToast();
  const [images, setImages] = useState([]);
  // Dictation: the words gather in a live transcript while the mic is on and
  // go into the field together when listening ends (a tap, silence, or an
  // error) — never piecemeal into what is being typed.
  const [transcript, setTranscript] = useState('');
  const transcriptRef = useRef('');
  const speech = useSpeech({ onText: (phrase) => { transcriptRef.current = transcriptRef.current ? `${transcriptRef.current} ${phrase}` : phrase; setTranscript(transcriptRef.current); } });
  const wasListening = useRef(false);
  useEffect(() => {
    if (wasListening.current && !speech.listening) {
      const words = transcriptRef.current.trim();
      transcriptRef.current = '';
      setTranscript('');
      if (words) setDraft((d) => (d.trim() ? `${d.replace(/\s+$/, '')} ${words}` : words));
    }
    wasListening.current = speech.listening;
  }, [speech.listening, setDraft]);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    return () => clearTimeout(t);
  }, [open, focusTick]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  // Resize by dragging the left edge.
  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width };
    const move = (ev) => { if (dragRef.current) setWidth(dragRef.current.startWidth + (dragRef.current.startX - ev.clientX)); };
    const up = () => { dragRef.current = null; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;

  const addFiles = async (files) => {
    const room = MAX_IMAGES - images.length;
    if (files.length > room) toast.warning(`Up to ${MAX_IMAGES} photos per message; the first ${Math.max(room, 0)} were kept.`);
    for (const file of files.slice(0, Math.max(room, 0))) {
      try { const att = await fileToAttachment(file); setImages((list) => [...list, att]); }
      catch (err) { toast.error(err.message || 'Could not read that image'); }
    }
  };

  const submit = () => {
    // Sending while dictating takes the transcript along with the typed words.
    const spoken = transcriptRef.current.trim();
    const words = spoken ? (draft.trim() ? `${draft.replace(/\s+$/, '')} ${spoken}` : spoken) : draft;
    if ((!words.trim() && images.length === 0) || busy) return;
    if (speech.listening) { transcriptRef.current = ''; setTranscript(''); speech.stop(); }
    send(words, images);
    setImages([]);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  // Only the latest meeting write-up is live; earlier drafts fold away.
  const latestReport = [...messages].reverse().find((m) => m.role === 'assistant' && m.proposals.some((p) => p.kind === 'meeting_report'));
  // "this" is the record on screen unless the person cleared it here.
  const contextName = viewing && (viewing.opportunity_name || viewing.contact_name || viewing.account_name);
  const pageName = pageContext && (pageContext.opportunity_name || pageContext.contact_name || pageContext.account_name);
  const starters = startersFor({ pathname: location.pathname, viewing });
  // What the assistant does, as chips above the composer: every AI function
  // starts here. A chip either asks straight away or puts words in the field.
  const prefill = (text) => { setDraft(text); setTimeout(() => inputRef.current && inputRef.current.focus(), 0); };
  const functions = [
    { id: 'meeting', label: 'Write up a meeting', icon: FileText, run: () => prefill(contextName ? `Write up my meeting with ${viewing.account_name}: ` : 'Write up my meeting with ') },
    { id: 'note', label: 'Log what happened', icon: PenLine, run: () => prefill(contextName ? `On ${viewing.account_name}: ` : '') },
    { id: 'brief', label: contextName ? 'Brief me on this account' : 'Brief me on an account', icon: BookOpen, run: () => (contextName ? send(`Brief me on ${viewing.account_name}`) : prefill('Brief me on ')) },
    { id: 'report', label: 'Run a report', icon: BarChart3, run: () => prefill('Pipeline by stage this quarter') },
    { id: 'today', label: "What's on my plate", icon: Sun, run: () => send("What's on my plate today?") },
  ];

  return (
    <aside className="assistant" style={{ width }} role="complementary" aria-label="AI Assistant">
      <div className="assistant-handle" onMouseDown={startDrag} role="separator" aria-orientation="vertical" aria-label="Resize the assistant" title="Drag to resize" />
      <header className="assistant-header">
        <h2 className="assistant-title"><Sparkles size={16} aria-hidden="true" /> AI Assistant</h2>
        {contextName ? (
          <span className="assistant-context" title="What the assistant assumes you mean by “this”">
            Viewing: {contextName}
            <button type="button" className="assistant-context-clear" onClick={clearViewing} aria-label={`Stop using ${contextName} as context`} title="Ask without this record in mind">
              <X size={11} aria-hidden="true" />
            </button>
          </span>
        ) : pageName ? (
          <button type="button" className="assistant-context assistant-context-off" onClick={restoreViewing} title="Ask with this record in mind again">
            Use {pageName}
          </button>
        ) : null}
        <span className="capture-spacer" />
        <button type="button" className="btn-icon" onClick={clear} aria-label="New conversation" title="New conversation" disabled={busy}><RotateCcw size={15} /></button>
        <button type="button" className="btn-icon" onClick={close} aria-label="Close the assistant" title={`Close (${modKey}/)`}><X size={17} /></button>
      </header>

      <div className="assistant-list" ref={listRef}>
        {messages.length === 0 && (
          <div className="assistant-empty">
            <p>Ask about any account, deal or person, or tell me what happened and I'll propose the entries. Everything the AI does starts from the buttons below. I only read what you can see, and I never change anything without your say-so.</p>
            {starters.length > 0 && (
              <div className="assistant-starters" role="group" aria-label="Try asking">
                <span className="assistant-starters-label">Try asking</span>
                {starters.map((q) => (
                  <button key={q} type="button" className="assistant-starter" onClick={() => send(q)} disabled={busy}>{q}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`assistant-msg assistant-msg-${m.role}`}>
            {m.role === 'user' ? (
              <div className="assistant-bubble">
                {m.text}
                {m.images && m.images.length > 0 && (
                  <div className="assistant-bubble-photos">
                    {m.images.map((img, i) => (img.previewUrl
                      ? <img key={i} src={img.previewUrl} alt={img.name || 'photo'} />
                      : <span key={i} className="assistant-bubble-photo-name"><Camera size={12} aria-hidden="true" /> {img.name || 'photo'}</span>))}
                  </div>
                )}
              </div>
            ) : (
              <div className="assistant-reply">
                {m.tools.map((t, i) => <ToolLine key={`${t.name}-${i}`} tool={t} />)}
                {(m.reports || []).map((r, i) => <ReportCard key={`${r.path}-${i}`} report={r} />)}
                {m.text && <Markdown text={m.text} />}
                {m.pending && (m.stepActive || (!m.text && m.tools.length === 0)) && !m.tools.some((t) => t.status === 'running') && (
                  <div className="asst-tool asst-tool-running asst-step" aria-live="polite"><Loader2 size={12} className="asst-spin" aria-hidden="true" /> {m.step || 'Reading your message'}…</div>
                )}
                {m.pending && m.text && !m.stepActive && <span className="assistant-cursor" aria-hidden="true" />}
                {m.proposals.length > 0 && <Proposals message={m} onDecide={decide} busy={busy} superseded={!!latestReport && latestReport.id !== m.id} onApproveMeeting={applyMeeting} onDismiss={dismissProposal} onAsk={send} onTick={setProposalAccepted} onDraftChange={saveMeetingDraft} onEditField={editProposal} />}
                {m.error && (
                  <div className="alert alert-error asst-error" role="alert">
                    <span>{m.error}</span>
                    {!m.pending && (
                      <span className="asst-error-actions">
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => retry(m.id)} disabled={busy}><RotateCcw size={12} aria-hidden="true" /> Try again</button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => editFailed(m.id)} disabled={busy}><PenLine size={12} aria-hidden="true" /> Edit message</button>
                      </span>
                    )}
                  </div>
                )}
                {!m.pending && !m.error && (m.usage || canRegenerate(m.id)) && (
                  <div className="assistant-usage">
                    {m.usage && <span>{usageLine(m)}</span>}
                    {canRegenerate(m.id) && (
                      <button type="button" className="asst-regen" onClick={() => regenerate(m.id)} title="Ask for a different answer to the same message">
                        <RefreshCw size={12} aria-hidden="true" /> Regenerate
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="assistant-functions" role="toolbar" aria-label="What the assistant can do">
        {functions.map((f) => <button key={f.id} type="button" className="chip" onClick={f.run} disabled={busy}><f.icon size={13} aria-hidden="true" /> {f.label}</button>)}
      </div>
      <form
        className="assistant-composer"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(imageFilesFrom(e.dataTransfer)); }}
      >
        {images.length > 0 && (
          <ul className="assistant-thumbs" aria-label="Attached photos">
            {images.map((img) => (
              <li key={img.id}>
                <img src={img.previewUrl} alt={img.name} />
                <button type="button" onClick={() => setImages((list) => list.filter((i) => i.id !== img.id))} aria-label={`Remove ${img.name}`}><X size={12} /></button>
              </li>
            ))}
          </ul>
        )}
        {speech.listening && (
          <div className="assistant-transcript" role="status" aria-live="polite">
            <span className="assistant-transcript-label"><Mic size={12} aria-hidden="true" /> Listening — the words go into the field when you stop</span>
            <p>
              {transcript}
              {speech.interim ? <em> {speech.interim}…</em> : null}
              {!transcript && !speech.interim ? <em>Say what happened…</em> : null}
            </p>
          </div>
        )}
        <div className="assistant-composer-row">
          <textarea
            ref={inputRef}
            className="assistant-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={(e) => { const files = imageFilesFrom(e.clipboardData); if (files.length) { e.preventDefault(); addFiles(files); } }}
            placeholder={hint === 'note'
              ? 'What happened? Who you spoke with, what was said, what comes next…'
              : contextName ? `Ask about ${contextName}, or tell me what happened…` : 'Ask about an account, or tell me what happened…'}
            rows={draft.length > 160 ? 5 : 2}
            aria-label="Message the assistant"
          />
          <div className="assistant-composer-tools">
            {speech.supported && (
              <button type="button" className={`btn btn-icon assistant-mic ${speech.listening ? 'is-live' : ''}`} onClick={speech.listening ? speech.stop : speech.start} aria-pressed={speech.listening} aria-label={speech.listening ? 'Stop dictating' : 'Dictate'} title={speech.listening ? 'Stop dictating' : 'Dictate'} disabled={busy}>
                {speech.listening ? <Square size={14} /> : <Mic size={15} />}
              </button>
            )}
            <label className={`btn btn-icon ${images.length >= MAX_IMAGES || busy ? 'is-disabled' : ''}`} title="Attach a photo">
              <ImagePlus size={15} aria-hidden="true" />
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden disabled={images.length >= MAX_IMAGES || busy} onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} aria-label="Attach a photo" />
            </label>
            {busy ? (
              <button type="button" className="btn btn-secondary" onClick={stop} aria-label="Stop"><Square size={14} /></button>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={!draft.trim() && images.length === 0} aria-label="Send"><Send size={14} /></button>
            )}
          </div>
        </div>
      </form>
      <p className="assistant-foot">Reads only what you can see. Writes only what you approve.</p>
    </aside>
  );
}
